import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Users, AlertTriangle, ArrowRight, Clock, TrendingUp, ClipboardCheck, FileText, UserPlus, Plus, BookOpen, Target } from 'lucide-react';
import { getModuleIcon, MODULES, getFamiliesForModule, getAllowedTiersForGroup, humanizeFamilyId } from '../../../content';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { PageLayout } from '../../shared/PageLayout';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { DjayNarrator } from '../../../mascot';
import { getWeeklyActivityForLearner } from '../../../utils/activityHelpers';
import { getAvatarGradient } from '../utils/avatarUtils';
import { V11_CONFIG } from '../../../mastery';
import { getFamilyMasteryKey } from '../../../mastery';
import { getStudentInsights, DifferentiationInsight } from '../utils/differentiationInsights';
import { GroupId } from '../../../config/systemConfig';
import { formatRelativeTime } from '../../../utils/formatters';
import { containerVariants, itemVariants, listContainerVariants, listItemVariants } from '../shared/animationVariants';
import { DataFreshness } from '../../shared/DataFreshness';
import { PriorityAlertBar, type PriorityAlert } from '../components/PriorityAlertBar';
/** Days of inactivity before a student is flagged as "long inactive" (MN-02) */
const LONG_INACTIVE_DAYS = 14;
const LONG_INACTIVE_MS = LONG_INACTIVE_DAYS * 24 * 60 * 60 * 1000;
// Simple animated number component (SC-3: added cancelAnimationFrame cleanup)
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    let startTime: number;
    let rafId: number;
    const duration = 1000; // 1 second
    const startValue = 0;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutQuart
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(Math.floor(startValue + easeProgress * (value - startValue)));
      if (progress < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        setDisplayValue(value);
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [value]);
  return <>{displayValue}</>;
}
interface TodayPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
  onNavigate: (route: string) => void;
  classroomName?: string;
}
export function TodayPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent,
  onNavigate,
  classroomName
}: TodayPageProps) {
  const [isAddStudentOpen, setIsAddStudentOpen] = useState(false);
  const [lastLoadedAt, setLastLoadedAt] = useState(() => new Date());
  const stats = useMemo(() => {
    if (students.length === 0) return null;
    const strugglingStudents: Array<{
      student: StudentData;
      insight: DifferentiationInsight;
    }> = [];
    let studentsPracticedThisWeek = 0;
    let totalMasterySum = 0;
    let totalMasteryCount = 0;
    const moduleMasterySums: Record<string, {
      sum: number;
      count: number;
    }> = {};
    MODULES.forEach(m => {
      moduleMasterySums[m.id] = {
        sum: 0,
        count: 0
      };
    });
    const allowedTiers = getAllowedTiersForGroup(selectedGroup);
    const familyStatsMap: Record<string, {
      familyId: string;
      moduleId: string;
      label: string;
      totalMastery: number;
      practicedCount: number;
      strugglingCount: number;
    }> = {};
    const inactiveStudents = students.filter(student => {
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      return weeklyActivity.filter(d => d.practiced).length === 0;
    }).map(student => {
      let lastActive = 0;
      if (student.data.questionStates) {
        for (const state of Object.values(student.data.questionStates)) {
          if (state.lastAttemptDate > lastActive) {
            lastActive = state.lastAttemptDate;
          }
        }
      }
      return {
        student,
        lastActive
      };
    }).sort((a, b) => b.lastActive - a.lastActive);
    // Group context is needed for insights, we can extract it from the first student's data if available
    // or fallback to 6. In a real app, selectedGroup would be passed as a prop.
    // Since TodayPage doesn't receive selectedGroup, we'll try to infer it or default to 6.
    // Actually, we can just pass 6 as a fallback, but let's try to get it from the first student's groupVersion if possible.
    // Wait, getStudentInsights needs a GroupId. Let's just use 6 as a fallback since we don't have it in props.
    // A better way: TodayPage is rendered inside TeacherShellV2 which has selectedGroup.
    // Let's add selectedGroup to TodayPageProps.
    students.forEach(student => {
      let hasPracticed = false;
      MODULES.forEach(module => {
        const mastery = getModuleMastery(student.id, module.id);
        if (mastery > 0) {
          hasPracticed = true;
          totalMasterySum += mastery;
          totalMasteryCount++;
          moduleMasterySums[module.id].sum += mastery;
          moduleMasterySums[module.id].count++;
        }
      });
      if (hasPracticed) {
        // Use the selectedGroup from props
        const insights = getStudentInsights(student.data, selectedGroup, 1);
        if (insights.length > 0 && insights[0].mastery < V11_CONFIG.STRUGGLING_THRESHOLD) {
          strugglingStudents.push({
            student,
            insight: insights[0]
          });
        }
      }
      const weeklyActivity = getWeeklyActivityForLearner(student.data.questionStates || {});
      if (weeklyActivity.some(d => d.practiced)) {
        studentsPracticedThisWeek++;
      }
      // Calculate family stats for class-wide weak spots
      MODULES.forEach(module => {
        const families = getFamiliesForModule(module.id);
        const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
        relevantFamilies.forEach(f => {
          const key = getFamilyMasteryKey(f.familyId, selectedGroup);
          const fm = student.data.familyMastery?.[key];
          if (fm && fm.attempts > 0) {
            if (!familyStatsMap[f.familyId]) {
              familyStatsMap[f.familyId] = {
                familyId: f.familyId,
                moduleId: module.id,
                label: humanizeFamilyId(f.familyId),
                totalMastery: 0,
                practicedCount: 0,
                strugglingCount: 0
              };
            }
            familyStatsMap[f.familyId].practicedCount++;
            familyStatsMap[f.familyId].totalMastery += fm.masteryScore;
            if (fm.masteryScore < V11_CONFIG.STRUGGLING_THRESHOLD) {
              familyStatsMap[f.familyId].strugglingCount++;
            }
          }
        });
      });
    });
    const classWideWeakSpots = Object.values(familyStatsMap).map(f => ({
      ...f,
      avgMastery: Math.round(f.totalMastery / f.practicedCount)
    })).filter(f => f.strugglingCount >= 2) // Only show if multiple students struggle
    .sort((a, b) => {
      if (b.strugglingCount !== a.strugglingCount) {
        return b.strugglingCount - a.strugglingCount;
      }
      return a.avgMastery - b.avgMastery;
    }).slice(0, 5);
    const classAverageMastery = totalMasteryCount > 0 ? Math.round(totalMasterySum / totalMasteryCount) : 0;
    let strongestModule = MODULES[0];
    let weakestClassModule = MODULES[0];
    let highestAvg = -1;
    let lowestAvg = 101;
    MODULES.forEach(m => {
      const mStats = moduleMasterySums[m.id];
      if (mStats.count > 0) {
        const avg = mStats.sum / mStats.count;
        if (avg > highestAvg) {
          highestAvg = avg;
          strongestModule = m;
        }
        if (avg < lowestAvg) {
          lowestAvg = avg;
          weakestClassModule = m;
        }
      }
    });
    if (highestAvg === -1) {
      strongestModule = MODULES[0];
      weakestClassModule = MODULES[0];
    }
    return {
      strugglingStudents: strugglingStudents.sort((a, b) => a.insight.mastery - b.insight.mastery).slice(0, 6),
      totalStruggling: strugglingStudents.length,
      inactiveStudents,
      studentsPracticedThisWeek,
      classAverageMastery,
      strongestModule,
      weakestClassModule,
      highestAvg,
      lowestAvg,
      classWideWeakSpots
    };
  }, [students, getModuleMastery, selectedGroup]);
  const alerts = useMemo(() => {
    if (!stats) return [];
    const newAlerts: PriorityAlert[] = [];
    stats.strugglingStudents.forEach(({
      student,
      insight
    }) => {
      newAlerts.push({
        id: `struggle-${student.id}`,
        tone: 'urgent',
        icon: AlertTriangle,
        message: `${student.name} loopt vast op ${insight.familyName}`,
        onClick: () => onSelectStudent(student.id)
      });
    });
    if (newAlerts.length < 5) {
      stats.inactiveStudents.forEach(({
        student,
        lastActive
      }) => {
        if (newAlerts.length >= 5) return;
        if (lastActive === 0 || Date.now() - lastActive > LONG_INACTIVE_MS) {
          newAlerts.push({
            id: `inactive-${student.id}`,
            tone: 'warning',
            icon: Clock,
            message: `${student.name} is al ${lastActive === 0 ? 'nog nooit' : formatRelativeTime(lastActive)} niet actief`,
            onClick: () => onSelectStudent(student.id)
          });
        }
      });
    }
    return newAlerts;
  }, [stats, onSelectStudent]);
  const hour = new Date().getHours();
  let greeting = 'Goedenavond';
  let greetingIcon = '🌙';
  if (hour >= 6 && hour < 12) {
    greeting = 'Goedemorgen';
    greetingIcon = '☀️';
  } else if (hour >= 12 && hour < 18) {
    greeting = 'Goedemiddag';
    greetingIcon = '🌤️';
  }
  const dateString = new Date().toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  if (students.length === 0) {
    return <PageLayout title="Vandaag" description="Overzicht van de klas" actions={<Button onClick={() => setIsAddStudentOpen(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-10374" data-mp="3lzffg27xeia2">
            <Plus className="w-4 h-4" data-id="element-10375" data-mp="2aj8k5fuqjekz" />
            Leerling Toevoegen
          </Button>} data-id="element-10373" data-mp="3q3n3ryah1w7r">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4
      }} data-id="element-10376" data-mp="25ouvdwakw5pa">
          <EmptyState title="Nog geen leerlingen" message="Je klas is nog leeg. Voeg leerlingen toe om hun voortgang te volgen en inzicht te krijgen in hun prestaties." emotion="thinking" actionLabel="Naar Mijn Klas" onAction={() => onNavigate('class')} colorScheme="indigo" data-id="element-10377" data-mp="u4ckinwe0n1h" />
        </motion.div>
      </PageLayout>;
  }
  if (!stats) return null;
  const StrongestIcon = getModuleIcon(stats.strongestModule.icon);
  const WeakestIcon = getModuleIcon(stats.weakestClassModule.icon);
  return <PageLayout title="Vandaag" description={classroomName ? `Actuele inzichten voor ${classroomName}` : 'Actuele inzichten en aandachtspunten'} data-id="element-10378" data-mp="3fl9t68zl5cyp">
      <motion.div className="max-w-5xl mx-auto space-y-6 pb-12" variants={containerVariants} initial="hidden" animate="visible" data-id="element-10379" data-mp="qqti8rqa1b9l">
        <div className="flex justify-end mb-[-12px]" data-id="element-10380" data-mp="zsb5cu23qd7s">
          <DataFreshness updatedAt={lastLoadedAt} onRefresh={() => setLastLoadedAt(new Date())} data-id="element-10381" data-mp="zt2ubm171cp3" />
        </div>

        {/* Welcome Block */}
        <motion.div variants={itemVariants} className="bg-indigo-600 rounded-2xl p-4 text-white shadow-md relative overflow-hidden" data-id="element-10382" data-mp="19pp9pvar3bjq">
          <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-4" data-id="element-10383" data-mp="1jgaigbk51wgd">
            <div data-id="element-10384" data-mp="1eh7aojcrxc4p">
              <h1 className="text-2xl font-black mb-1" data-id="element-10385" data-mp="1egnup86ooxtb">
                {greetingIcon} {greeting}!
              </h1>
              <p className="text-indigo-100 font-medium text-sm" data-id="element-10386" data-mp="1si07jaqjsjaz">
                {classroomName || 'Mijn Klas'} •{' '}
                <span className="capitalize" data-id="element-10387" data-mp="1njgmdcjs3bjo">{dateString}</span>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm font-medium bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/10" data-id="element-10388" data-mp="274709m0igzv7">
              <div className="flex items-center gap-1.5" data-id="element-10389" data-mp="1y3io6wuoh9ky">
                <Users className="w-4 h-4 text-indigo-200" data-id="element-10390" data-mp="1y5224gsv38jk" />
                <span data-id="element-10391" data-mp="223jepfv1xb8d">{students.length} leerlingen</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-10392" data-mp="1oco0rp5p43xx"></div>
              <div className="flex items-center gap-1.5" data-id="element-10393" data-mp="1t3mimt6c1a3a">
                <TrendingUp className="w-4 h-4 text-emerald-300" data-id="element-10394" data-mp="1t57c0fgwumzk" />
                <span data-id="element-10395" data-mp="1eg1fx0dddcvw">{stats.studentsPracticedThisWeek} actief</span>
              </div>
              <div className="w-1 h-1 rounded-full bg-indigo-300 hidden sm:block" data-id="element-10396" data-mp="1jdjo2ojkbo2u"></div>
              <div className="flex items-center gap-1.5" data-id="element-10397" data-mp="15ejzthzypb77">
                <AlertTriangle className={`w-4 h-4 ${stats.totalStruggling > 0 ? 'text-rose-300' : 'text-indigo-200'}`} data-id="element-10398" data-mp="15f3ix21cm5mt" />
                <span data-id="element-10399" data-mp="12cyuaelc7991">{stats.totalStruggling} aandachtspunten</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Priority Alerts */}
        <motion.div variants={itemVariants} data-id="element-10400" data-mp="smvyy7qqtj7j">
          <PriorityAlertBar alerts={alerts} maxVisible={3} data-id="element-10401" data-mp="slkxiekrsnjl" />
        </motion.div>

        {/* Quick Actions Row */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10402" data-mp="ol24ih755bft">
          <button onClick={() => onNavigate('homework')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10403" data-mp="jjuyonok6mbs">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors" data-id="element-10404" data-mp="5nreln7efud1">
              <BookOpen className="w-5 h-5" data-id="element-10405" data-mp="2007m0rbxlejh" />
            </div>
            <div data-id="element-10406" data-mp="1zymmenarf5er">
              <div className="font-bold text-slate-900 group-hover:text-blue-700 transition-colors" data-id="element-10407" data-mp="3hyccvx4rmosd">
                Huiswerk
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10408" data-mp="3mvm8xvmntyp6">
                Naar overzicht
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('assessments')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10409" data-mp="3vp8xp1sga1ov">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors" data-id="element-10410" data-mp="340fbmnfd9u7d">
              <ClipboardCheck className="w-5 h-5" data-id="element-10411" data-mp="3d0khgt4wwkjf" />
            </div>
            <div data-id="element-10412" data-mp="nagtsvus9krn">
              <div className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors" data-id="element-10413" data-mp="n8w1zdzusfxh">
                Nieuwe toets
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10414" data-mp="2obwuak0d67iu">
                Toets inplannen
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('reports')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10415" data-mp="2k9s3dt1avozw">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-600 group-hover:text-white transition-colors" data-id="element-10416" data-mp="25kvyg4ujis3i">
              <FileText className="w-5 h-5" data-id="element-10417" data-mp="2fb8hfsmpid7j" />
            </div>
            <div data-id="element-10418" data-mp="2fcjk1p4f1ex2">
              <div className="font-bold text-slate-900 group-hover:text-emerald-700 transition-colors" data-id="element-10419" data-mp="2fd1p9b9233h4">
                Rapporten
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10420" data-mp="1mtw38lwgcm1s">
                Resultaten bekijken
              </div>
            </div>
          </button>
          <button onClick={() => onNavigate('management')} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-indigo-300 transition-all group text-left" data-id="element-10421" data-mp="3tj6hna768wvy">
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-600 group-hover:text-white transition-colors" data-id="element-10422" data-mp="3kk2x01scw9fc">
              <UserPlus className="w-5 h-5" data-id="element-10423" data-mp="g5cwqajziw01" />
            </div>
            <div data-id="element-10424" data-mp="l3ecomm46f7a">
              <div className="font-bold text-slate-900 group-hover:text-amber-700 transition-colors" data-id="element-10425" data-mp="l3whw8qr83rc">
                Leerling toevoegen
              </div>
              <div className="text-xs text-slate-500 font-medium" data-id="element-10426" data-mp="75pujmjflm5x">
                Klas beheren
              </div>
            </div>
          </button>
        </motion.div>

        {/* Class Overview Card */}
        <motion.div variants={itemVariants} data-id="element-10427" data-mp="2rw74l99tl3od">
          <Card className="bg-white border-slate-200 shadow-sm overflow-hidden p-5" data-id="element-10428" data-mp="2wt77jbbdhqb9">
            <div className="flex items-center gap-2 mb-4" data-id="element-10429" data-mp="3njio4dzjbwf9">
              <TrendingUp className="w-5 h-5 text-indigo-600" data-id="element-10430" data-mp="3ngwfeb0wravg" />
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wider" data-id="element-10431" data-mp="3jgmaz2srksip">
                Klas Overzicht
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-slate-100" data-id="element-10432" data-mp="39j7x9ay86hu3">
              <div className="flex flex-col" data-id="element-10433" data-mp="b1vjeqc739ex">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10434" data-mp="b0swsulwnt4t">
                  Gemiddelde beheersing
                </div>
                <div className="flex items-end gap-2" data-id="element-10435" data-mp="1b0zhsmf8lwn">
                  <div className="text-3xl font-black text-slate-900" data-id="element-10436" data-mp="19g1tu4t8muk">
                    <AnimatedNumber value={stats.classAverageMastery} data-id="element-10437" data-mp="3shkdthrgutzp" />%
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0" data-id="element-10438" data-mp="p1ddnl89fevo">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10439" data-mp="p02annmn2b5m">
                  Sterkste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-10440" data-mp="11b30gykl6z73">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center" data-id="element-10441" data-mp="11dfrs2c8ilwt">
                    <StrongestIcon className="w-4 h-4" data-id="element-10442" data-mp="1b9isr4vcoei6" />
                  </div>
                  <div data-id="element-10443" data-mp="1b7fszt4eb709">
                    <div className="font-bold text-slate-900" data-id="element-10444" data-mp="1fbnm02hvz94a">
                      {stats.strongestModule.title}
                    </div>
                    <div className="text-xs font-medium text-emerald-600" data-id="element-10445" data-mp="1k8w2zzrat73y">
                      {stats.highestAvg > -1 ? Math.round(stats.highestAvg) : 0}
                      % beheersing
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:pl-6 pt-4 md:pt-0 cursor-pointer hover:bg-slate-50 rounded-xl p-2 -m-2 transition-colors" onClick={() => onNavigate('subjects')} data-id="element-10446" data-mp="1z0eg9psrwnjm">
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1" data-id="element-10447" data-mp="32in20yg2so32">
                  Zwakste vak
                </div>
                <div className="flex items-center gap-2 mt-1" data-id="element-10448" data-mp="3cfhzde0m4ifr">
                  <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center" data-id="element-10449" data-mp="3chcicmsbq0h7">
                    <WeakestIcon className="w-4 h-4" data-id="element-10450" data-mp="3geieomjvt2y0" />
                  </div>
                  <div data-id="element-10451" data-mp="3lfo7slhges82">
                    <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-10452" data-mp="3qf14dxy4ndnu">
                      {stats.weakestClassModule.title}
                    </div>
                    <div className="text-xs font-medium text-rose-600" data-id="element-10453" data-mp="3v83pdhou9esf">
                      {stats.lowestAvg < 101 ? Math.round(stats.lowestAvg) : 0}%
                      beheersing
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Class-wide Weak Spots */}
        {stats.classWideWeakSpots.length > 0 && <motion.section variants={itemVariants} data-id="element-10454" data-mp="1sjc36ojxcc0z">
            <div className="flex items-center justify-between mb-4" data-id="element-10455" data-mp="1shiyvfwsa3w5">
              <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-10456" data-mp="1ogpbcnn96r3s">
                <Target className="w-4 h-4 text-indigo-500" data-id="element-10457" data-mp="1jjpd2ywnasn8" />
                Klas-brede Zwakke Plekken
              </h2>
            </div>
            <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10458" data-mp="2gahkz99fdedx">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10459" data-mp="2c7uwnibyqblh">
                <div className="w-8" data-id="element-10460" data-mp="22h00nm8wzxmu"></div>
                <div data-id="element-10461" data-mp="2zwdybf0ibq4l">Onderdeel</div>
                <div className="text-right w-32" data-id="element-10462" data-mp="2zxfic8zi3myg">Moeite mee</div>
                <div className="w-6" data-id="element-10463" data-mp="2v5dzg47tuxzg"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-10464" data-mp="2v3ui0aafqgw3">
                {stats.classWideWeakSpots.map((spot, i) => {
              const moduleDef = MODULES.find(m => m.id === spot.moduleId);
              const IconComp = getModuleIcon(moduleDef?.icon || 'calculator');
              return <motion.div key={spot.familyId} variants={listItemVariants} onClick={() => onNavigate('subjects')} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10465" data-mp="2ve15vn2vx1pp" data-mpi={i}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${moduleDef?.bgColor || 'bg-slate-100'} ${moduleDef?.color || 'text-slate-500'}`} data-id="element-10466" data-mp="okyoddy1q83g">
                        <IconComp className="w-4 h-4" data-id="element-10467" data-mp="252gx49yxnn7a" />
                      </div>
                      <div className="min-w-0 flex flex-col gap-1" data-id="element-10468" data-mp="2isst4jyn263p">
                        <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-10469" data-mp="2iv5ihi6q7iqs">
                          {spot.label}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-10470" data-mp="2stcvjpxw9gni">
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10471" data-mp="2nrdsyi9zanrm">
                            <div className={`h-full rounded-full ${spot.avgMastery >= 75 ? 'bg-emerald-500' : spot.avgMastery >= 40 ? 'bg-indigo-400' : 'bg-rose-500'}`} style={{
                        width: `${Math.max(2, spot.avgMastery)}%`
                      }} data-id="element-10472" data-mp="31qn9jj23jjw4" />
                          </div>
                          <span className="text-[10px] font-bold text-slate-400" data-id="element-10473" data-mp="14sxz3c1h50i4">
                            {spot.avgMastery}% gem.
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-end w-32" data-id="element-10474" data-mp="1ds2vabpkchr4">
                        <span className="font-bold text-xs text-rose-600 bg-rose-50 px-2 py-1 rounded-md" data-id="element-10475" data-mp="1isqn2zlm92me">
                          {spot.strugglingCount} van {spot.practicedCount} lln
                        </span>
                      </div>
                      <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-10476" data-mp="v3o4npj5r5qu">
                        <ArrowRight className="w-4 h-4" data-id="element-10477" data-mp="zvpg598i4sft" />
                      </div>
                    </motion.div>;
            })}
              </motion.div>
            </Card>
          </motion.section>}

        {/* Aandachtspunten is now covered by PriorityAlertBar above.
            Only show a compact "see all" affordance when overflow exists. */}
        {stats.totalStruggling > 3 && <motion.div variants={itemVariants} className="text-center -mt-2" data-id="element-10478" data-mp="3qz192qhp69j1">
            <button onClick={() => onNavigate('class')} className="text-sm font-bold text-indigo-600 hover:text-indigo-700 transition-colors inline-flex items-center gap-1.5 group" data-id="element-10479" data-mp="3lzy230i5pt8f">
              Bekijk alle {stats.totalStruggling} leerlingen die aandacht nodig
              hebben
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-10480" data-mp="33baw8fs5ozts" />
            </button>
          </motion.div>}

        {/* Inactive Students */}
        <motion.section variants={itemVariants} data-id="element-10481" data-mp="2ya3pmk1r26op">
          <div className="flex items-center justify-between mb-4" data-id="element-10482" data-mp="2tddb415lfj5e">
            <h2 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-10483" data-mp="2pbi9evjr33r3">
              <Clock className="w-4 h-4 text-amber-500" data-id="element-10484" data-mp="2eqob7y7fj3yh" />
              Niet geoefend deze week
            </h2>
          </div>
          {stats.inactiveStudents.length > 0 ? <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10485" data-mp="2jn51axy6vse7">
              <div className="grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-10486" data-mp="25rt5soc7lxw8">
                <div className="w-8" data-id="element-10487" data-mp="2yf752t3dr5ji"></div>
                <div data-id="element-10488" data-mp="32fr0ns8b213j">Naam</div>
                <div className="text-right w-32" data-id="element-10489" data-mp="32epfguxkry83">Inactief sinds</div>
                <div className="w-6" data-id="element-10490" data-mp="2op44o7txipb5"></div>
              </div>
              <motion.div className="divide-y divide-slate-100" variants={listContainerVariants} initial="hidden" animate="visible" data-id="element-10491" data-mp="2omhvy4vay3rc">
                {stats.inactiveStudents.slice(0, 5).map(({
              student,
              lastActive
            }, i) => {
              const inactiveLabel = lastActive > 0 ? formatRelativeTime(lastActive) : 'Nog nooit geoefend';
              return <motion.div key={student.id} variants={listItemVariants} onClick={() => onSelectStudent(student.id)} className={`grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center px-4 py-2.5 cursor-pointer hover:bg-indigo-50/50 transition-colors group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-10492" data-mp="925th57qy0wl" data-mpi={i}>
                        <div className="relative" data-id="element-10493" data-mp="3mjerj251r0ra">
                          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarGradient(student.name)} flex items-center justify-center text-white font-bold text-sm shadow-sm`} data-id="element-10494" data-mp="3hir56trrj83e">
                            {student.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white bg-amber-500 shadow-sm" role="img" aria-label="Inactief deze week" data-id="element-10495" data-mp="2raup6dlfmbyn" />
                        </div>
                        <div className="min-w-0" data-id="element-10496" data-mp="39zhu8w3lz1c8">
                          <div className="font-bold text-sm text-slate-900 truncate group-hover:text-indigo-700 transition-colors" data-id="element-10497" data-mp="39ypztolqz9nj">
                            {student.name}
                          </div>
                        </div>
                        <div className="flex items-center justify-end w-32" data-id="element-10498" data-mp="3iyd2edq7qli6">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-md ${lastActive === 0 ? 'bg-rose-50 text-rose-600' : Date.now() - lastActive > LONG_INACTIVE_MS ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-600'}`} data-id="element-10499" data-mp="3nyhjo5yy3k9g">
                            {inactiveLabel}
                          </span>
                        </div>
                        <div className="text-slate-300 w-6 flex justify-end group-hover:text-indigo-500 transition-colors group-hover:translate-x-1 duration-200" data-id="element-10500" data-mp="1h9p84x5e8lw6">
                          <ArrowRight className="w-4 h-4" data-id="element-10501" data-mp="17dvoktt9bs4w" />
                        </div>
                      </motion.div>;
            })}
              </motion.div>
              {stats.inactiveStudents.length > 5 && <div className="p-3 bg-slate-50 border-t border-slate-100 text-center" data-id="element-10502" data-mp="1zzf7zyo0jxuy">
                  <button onClick={() => onNavigate('class')} className="text-xs font-bold text-slate-500 hover:text-indigo-600 uppercase tracking-wider transition-colors inline-flex items-center gap-1 group" data-id="element-10503" data-mp="1v0lsy1t2vyso">
                    + {stats.inactiveStudents.length - 5} meer bekijken
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" data-id="element-10504" data-mp="3ahvnmfmy21vi" />
                  </button>
                </div>}
            </Card> : <Card className="p-6 text-center border-emerald-100 bg-emerald-50/50 shadow-sm overflow-hidden flex flex-col items-center justify-center" data-id="element-10505" data-mp="3k8qanm80l5i0">
              <DjayNarrator size="sm" emotion="happy" data-id="element-10506" data-mp="3fg6zevwsumgd" />
              <p className="text-emerald-800 font-bold mt-3" data-id="element-10507" data-mp="3t850qkk9re1z">
                Iedereen heeft deze week geoefend!
              </p>
            </Card>}
        </motion.section>
      </motion.div>
    </PageLayout>;
}